import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import styled from '@emotion/styled';
import { Button } from '@/components/atoms/Button';
import { computeText } from '@/locales/utils';
import { WeekDay } from '@/services/innovorder/restaurant/restaurant.type';
import { generateTimeOptions, generateMassScheduleSlots, DAY_LABELS } from '../schedule.utils';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
    padding: ${({ theme }) => theme.spacing[3]};
    border: 1px solid ${({ theme }) => theme.color.grey200};
    border-radius: ${({ theme }) => theme.radius.main};
    background: ${({ theme }) => theme.color.grey100};
    margin-top: ${({ theme }) => theme.spacing[3]};
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing[2]};
    margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const Row = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[2]};
    justify-content: space-between;
`;

const Title = styled.span`
    font-size: ${({ theme }) => theme.font.size.normal};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    color: ${({ theme }) => theme.color.black400};
`;

const DayPickerRow = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[2]};
    align-items: center;
`;

const DayCircle = styled.button<{ isSelected: boolean }>`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => theme.color.slate300};
    background: ${({ theme, isSelected }) => (isSelected ? theme.color.slate300 : 'transparent')};
    color: ${({ theme, isSelected }) => (isSelected ? theme.color.white100 : theme.color.slate300)};
    font-size: ${({ theme }) => theme.font.size.small};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;

    &:hover {
        background: ${({ theme, isSelected }) => (isSelected ? theme.color.slate200 : theme.color.grey200)};
    }
`;

const Description = styled.p`
    font-size: ${({ theme }) => theme.font.size.small};
    color: ${({ theme }) => theme.color.black200};
    margin: 0;
    line-height: 1.5;
`;

const FieldGroup = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 4px;
`;

const FieldLabel = styled.span`
    font-size: ${({ theme }) => theme.font.size.small};
    color: ${({ theme }) => theme.color.black200};
`;

const StyledSelect = styled.select`
    height: 36px;
    padding: 0 ${({ theme }) => theme.spacing[2]};
    border: 1px solid ${({ theme }) => theme.color.grey200};
    border-radius: ${({ theme }) => theme.radius.main};
    font-size: ${({ theme }) => theme.font.size.small};
    background: ${({ theme }) => theme.color.white100};
    appearance: none;
    padding-right: 28px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 8px center;
`;

const OverwriteLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: ${({ theme }) => theme.font.size.small};
    color: ${({ theme }) => theme.color.black400};
    cursor: pointer;
    white-space: nowrap;
`;

const OverwriteCheckbox = styled.input`
    width: 18px;
    height: 18px;
    accent-color: ${({ theme }) => theme.color.emerald};
    cursor: pointer;
`;

const ErrorMessage = styled.span`
    font-size: ${({ theme }) => theme.font.size.small};
    color: ${({ theme }) => theme.color.bloodyMary};
`;

type MassScheduleProps = {
    onApply: (slots: { day: WeekDay; start: number; end: number }[], overwrite: boolean) => void;
};

const ALL_DAYS: WeekDay[] = [WeekDay.MON, WeekDay.TUE, WeekDay.WED, WeekDay.THU, WeekDay.FRI, WeekDay.SAT, WeekDay.SUN];

const GRANULARITY_OPTIONS = [
    { value: 15, labelId: 'restaurantInfo.schedule.mass.15min' },
    { value: 30, labelId: 'restaurantInfo.schedule.mass.30min' },
    { value: 60, labelId: 'restaurantInfo.schedule.mass.1h' },
];

const timeOptions = generateTimeOptions();

export const MassSchedule: React.FunctionComponent<MassScheduleProps> = ({ onApply }) => {
    const intl = useIntl();
    const [selectedDays, setSelectedDays] = useState<Set<WeekDay>>(new Set([]));
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('21:00');
    const [granularity, setGranularity] = useState(15);
    const [overwrite, setOverwrite] = useState(false);
    const [error, setError] = useState('');

    const endTimeOptions = useMemo(() => {
        return [...timeOptions.filter((t) => t > startTime), '00:00'];
    }, [startTime]);

    useEffect(() => {
        if (endTimeOptions.length > 0 && !endTimeOptions.includes(endTime)) {
            setEndTime(endTimeOptions[0]);
        }
    }, [endTimeOptions, endTime]);

    const toggleDay = useCallback((day: WeekDay) => {
        setSelectedDays((prev) => {
            const next = new Set(prev);
            if (next.has(day)) next.delete(day);
            else next.add(day);
            return next;
        });
    }, []);

    const handleApply = useCallback(() => {
        setError('');
        if (selectedDays.size === 0) {
            setError(computeText(intl, 'restaurantInfo.schedule.mass.noDays'));
            return;
        }
        const slots = generateMassScheduleSlots(Array.from(selectedDays), startTime, endTime, granularity);
        onApply(slots, overwrite);
    }, [selectedDays, startTime, endTime, granularity, overwrite, onApply, intl]);

    return (
        <Wrapper>
            <Column>
                <Title>{computeText(intl, 'restaurantInfo.schedule.autoEdit')}</Title>
                <Description>{computeText(intl, 'restaurantInfo.schedule.autoEditDescription')}</Description>
            </Column>

            <Column>
                <DayPickerRow>
                    {ALL_DAYS.map((day) => (
                        <DayCircle
                            key={day}
                            type="button"
                            isSelected={selectedDays.has(day)}
                            onClick={() => toggleDay(day)}
                        >
                            {computeText(intl, DAY_LABELS[day]).charAt(0)}
                        </DayCircle>
                    ))}
                </DayPickerRow>
            </Column>
            <Column>
                <Row>
                    <FieldGroup>
                        <FieldLabel>{computeText(intl, 'restaurantInfo.schedule.mass.startHour')}</FieldLabel>
                        <StyledSelect value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                            {timeOptions.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </StyledSelect>
                    </FieldGroup>

                    <FieldGroup>
                        <FieldLabel>{computeText(intl, 'restaurantInfo.schedule.mass.endHour')}</FieldLabel>
                        <StyledSelect value={endTime} onChange={(e) => setEndTime(e.target.value)}>
                            {endTimeOptions.map((t) => (
                                <option key={t} value={t}>
                                    {t === '00:00'
                                        ? `00:00 (${computeText(intl, 'restaurantInfo.schedule.mass.midnight')})`
                                        : t}
                                </option>
                            ))}
                        </StyledSelect>
                    </FieldGroup>

                    <FieldGroup>
                        <FieldLabel>{computeText(intl, 'restaurantInfo.schedule.mass.granularity')}</FieldLabel>
                        <StyledSelect value={granularity} onChange={(e) => setGranularity(Number(e.target.value))}>
                            {GRANULARITY_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {computeText(intl, opt.labelId)}
                                </option>
                            ))}
                        </StyledSelect>
                    </FieldGroup>
                </Row>

                <Row>
                    <OverwriteLabel>
                        <OverwriteCheckbox
                            type="checkbox"
                            checked={overwrite}
                            onChange={(e) => setOverwrite(e.target.checked)}
                        />
                        {computeText(intl, 'restaurantInfo.schedule.mass.overwrite')}
                    </OverwriteLabel>

                    <Button buttonType="primary" onClick={handleApply}>
                        {computeText(intl, 'restaurantInfo.schedule.mass.apply')}
                    </Button>
                </Row>
            </Column>

            {error && <ErrorMessage>{error}</ErrorMessage>}
        </Wrapper>
    );
};
